import { useTranslation } from 'next-i18next';

import { V1A_NAMESPACE } from '../constants';
import { useCountUp } from '../hooks/use-count-up';
import { useHtmlLang } from '../hooks/use-html-lang';
import { formatInteger } from './media';

const NUM_CLASS = 'v1-stat__num tabular m-0 max-w-full whitespace-nowrap text-[length:min(clamp(1.5rem,.9rem_+_2.1vw,2.5rem),17cqw)] font-bold leading-[1] tracking-[-.035em]';
const LABEL_CLASS = 'v1-stat__label mt-2 text-[length:var(--t-micro)] font-semibold uppercase tracking-[.12em] text-[#CBD5E1]';
const NOTE_CLASS = 'v1-stat__note mt-1 text-[length:var(--t-micro)] leading-[1.4] text-[#8C95A8]';
const PLUS = <span className='v1-stat__plus !text-[#93FF18] forced-colors:!text-[LinkText]'>+</span>;

function Spark() {
  return (
    <span className='v1-stat__spark' aria-hidden='true'>
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}

export function Stats() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const htmlLang = useHtmlLang();

  // the unit travels with the number and agrees with it ("1 milhão", "3 milhões"): no broken first frame
  const [playersRef, players] = useCountUp<HTMLSpanElement>(3, { from: 1, format: (value) => t('stats.players.value', { count: value }) });
  const [serversRef, servers] = useCountUp<HTMLSpanElement>(2000, { format: (value) => formatInteger(htmlLang, value) });
  const [countriesRef, countries] = useCountUp<HTMLSpanElement>(150, { format: (value) => formatInteger(htmlLang, value) });
  const [pingRef, ping] = useCountUp<HTMLSpanElement>(80, { format: (value) => formatInteger(htmlLang, value) });

  return (
    <section className='v1-stats relative isolate py-[clamp(1.75rem,1rem_+_2vw,2.75rem)]' aria-label={t('stats.aria')}>
      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        {/* role=list: list-style none drops the list semantics in Safari/VoiceOver, as the design notes */}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul className='v1-stats__grid m-0 grid list-none grid-cols-2 gap-x-4 gap-y-5 !p-0 *:min-w-0 max-[288px]:grid-cols-1 lg:grid-cols-4 lg:gap-6' role='list' data-reveal-group=''>
          <li className='v1-stat relative min-w-0 ps-[min(1rem,10%)] [container-type:inline-size]'>
            <Spark />
            <p className={`${NUM_CLASS} text-[#FFFFFF]`}>
              <span ref={playersRef}>{players}</span>
              {PLUS}
            </p>
            <p className={LABEL_CLASS}>{t('stats.players.label')}</p>
            <p className={NOTE_CLASS}>{t('stats.players.note')}</p>
          </li>
          <li className='v1-stat relative min-w-0 ps-[min(1rem,10%)] [container-type:inline-size]'>
            <Spark />
            <p className={`${NUM_CLASS} text-[#FFFFFF]`}>
              <span ref={serversRef}>{servers}</span>
              {PLUS}
            </p>
            <p className={LABEL_CLASS}>{t('stats.servers.label')}</p>
            <p className={NOTE_CLASS}>{t('stats.servers.note')}</p>
          </li>
          <li className='v1-stat relative min-w-0 ps-[min(1rem,10%)] [container-type:inline-size]'>
            <Spark />
            <p className={`${NUM_CLASS} text-[#FFFFFF]`}>
              <span ref={countriesRef}>{countries}</span>
              {PLUS}
            </p>
            <p className={LABEL_CLASS}>{t('stats.countries.label')}</p>
            <p className={NOTE_CLASS}>{t('stats.countries.note')}</p>
          </li>
          <li className='v1-stat v1-stat--accent relative min-w-0 ps-[min(1rem,10%)] [container-type:inline-size]'>
            <Spark />
            <p className={`${NUM_CLASS} !text-[#93FF18]`}>
              <span className='v1-stat__pre me-[.35em] align-[.32em] text-[length:max(.45em,11px)] font-semibold uppercase tracking-[.06em] text-[#8C95A8]'>{t('stats.ping.pre')}</span>
              <span ref={pingRef}>{ping}</span>%
            </p>
            <p className={LABEL_CLASS}>{t('stats.ping.label')}</p>
            <p className={NOTE_CLASS}>{t('stats.ping.note')}</p>
          </li>
        </ul>
      </div>
    </section>
  );
}
